import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { formatCurrency } from "@/utils/currency";

interface InstallmentBreakdownDrawerProps {
  promoCodeDiscount?: number;
  totalPrice: number;
  open: boolean;
  onClose: () => void;
}

const InstallmentBreakdownDrawer = ({
  promoCodeDiscount,
  totalPrice,
  open,
  onClose,
}: InstallmentBreakdownDrawerProps) => {
  const intl = useIntl();
  const { locale } = useLocaleInfo();
  const { selectedInstallments } = usePaymentContext();

  const nextPaymentMonth = dayjs()
    .add(1, "month")
    .locale(locale)
    .format("MMMM");

  return (
    <SwipeableDrawer open={open} onClose={onClose} disableRestoreFocus>
      <div className="pt-2">
        <div className="mb-6 text-center text-base font-medium">
          {intl.formatMessage({
            defaultMessage: "Instalment price breakdown",
            description:
              "Section heading for the detailed instalment payment breakdown",
          })}
        </div>
        <div className="mb-6 flex flex-col gap-3 text-sm">
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Number of instalments",
                description:
                  "Label for the number of instalments in the payment plan",
              })}
            </span>
            <span className="font-bold">
              {intl.formatMessage(
                {
                  defaultMessage: "{count} months",
                  description: "Number of months for the instalment plan",
                },
                { count: selectedInstallments },
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Next payment",
                description:
                  "Label for the date of the next scheduled instalment payment",
              })}
            </span>
            <span className="font-bold">{nextPaymentMonth}</span>
          </div>
          {promoCodeDiscount ? (
            <div className="flex justify-between">
              <span>
                {intl.formatMessage({
                  defaultMessage: "Promo code discount",
                  description:
                    "Line item label for the promo code discount in the price summary",
                })}
              </span>
              <div className="font-bold text-core-accent-500">
                {formatCurrency(promoCodeDiscount)}
              </div>
            </div>
          ) : null}
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Monthly payment",
                description:
                  "Label for the monthly instalment amount in the payment breakdown",
              })}
            </span>
            <span className="font-bold">
              {formatCurrency(totalPrice / selectedInstallments)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Total",
                description: "Generic 'Total' label in price summary tables",
              })}
            </span>
            <span className="font-bold">{formatCurrency(totalPrice)}</span>
          </div>
        </div>

        <Button fullWidth variant="contained" onClick={onClose}>
          {intl.formatMessage({
            defaultMessage: "Close",
            description:
              "Generic 'Close' button label to dismiss a drawer or dialog",
          })}
        </Button>
      </div>
    </SwipeableDrawer>
  );
};

export default InstallmentBreakdownDrawer;
